import { useContext, useEffect, useState, } from "react";
import { useNavigate } from "react-router-dom";

import ListPage from "../../../shared/components/list-page/list-page";
import { IColumn } from "../../../shared/components/normal-table/normal-table";
import Spinner from "../../../shared/components/spinner/spinner";
import { roleWrite, roleAdmin, roleRead } from "../../../shared/constants/user-roles";
import AuthContext from "../../../shared/context/auth-context";
import useHttp from "../../../shared/hooks/use-http";
import { getAllData, deleteMultiple, IListPageData } from "../../../shared/utils/api";
import { validateLoadingStatusNew } from "../../../shared/utils/utils";

import "./dataset-list.scss";

const columns: IColumn[] = [
  {
    key: "dataset_name",
    displayName: "Name",
    dataType: {
      type: "text",
    },
  },
  {
    key: "model_type",
    displayName: "Model type",
    dataType: {
      type: "text",
    },
  },
  {
    key: "timeframe",
    displayName: "Timeframe",
    dataType: {
      type: "text",
    },
    nonSortable: true
  },
];

const DatasetList = () => {
  const [data, setData] = useState<IListPageData | undefined>(undefined);
  const [unblockInput, setUnblockInput] = useState(false);
  const [sortedBy, setSortedBy] = useState("?order_by=dataset_name asc");
  const [currentOffset, setCurrentOffset] = useState(1);
  const authCtx = useContext(AuthContext);
  const navigate = useNavigate();
  const { sendRequest: getSortBy, status: getSortByStatus, data: getSortByData, error: errorGetSortByData } = useHttp(getAllData, true, authCtx.token, undefined, "analytics");
  const { sendRequest: deleteMultipleRequest, status: allStatusDelete, data: loadedDataDelete, error: errorDelete } = useHttp(deleteMultiple, false, authCtx.token, undefined, "analytics");
  const { sendRequest: getSortByPagination, status: getSortByPaginationStatus, data: getSortByPaginationData, error: getSortByPaginationError } = useHttp(getAllData, false, authCtx.token, undefined, "analytics");

  useEffect(() => {
    getSortBy("datasets", "?order_by=dataset_name asc&limit=50&offset=0");
  }, [getSortBy]);

  const onTableScrollAction = (offset: number) => {
    if (data && data.rows.length >= 50) {
      setCurrentOffset(offset);
      if (getSortByPaginationData?.rows.length !== 0) {
        getSortByPagination("datasets", `${sortedBy}&limit=50&offset=${offset * 50}`);
      }
    }
  };

  useEffect(() => {
    if (getSortByPaginationStatus === "completed" && !getSortByPaginationError) {
      data && setData({rows: [...data.rows, ...getSortByPaginationData.rows]});
    }
  }, [getSortByPaginationStatus]);

  useEffect(() => {
    const rolesArray = authCtx.selectedTenant.roles;
    if (rolesArray.indexOf(roleWrite) !== -1 || rolesArray.indexOf(roleAdmin) !== -1) {
      setUnblockInput(true);
    }
  }, [authCtx.selectedTenant, authCtx.systemAdmin]);

  useEffect(() => {
    if (getSortByStatus === "completed" && !errorGetSortByData) {
      setData(getSortByData);
    }
  }, [getSortByStatus]);

  useEffect(() => {
    if (allStatusDelete === "completed" && !errorDelete) {
      setData(loadedDataDelete);
      authCtx.setOnboardingAnalyticsToggleHandler();
    }
  }, [allStatusDelete]);

  const handleOnDelete = (ids: string[]) => {
    deleteMultipleRequest(`datasets${sortedBy}&limit=${currentOffset * 50}&offset=0`, { ids });
  };

  const onRowClickAction = (id: string) => {
    const rolesArray = authCtx.selectedTenant.roles;
    if ((rolesArray.length === 1 && rolesArray.indexOf(roleAdmin) !== -1)) {
      navigate(`/analytics/datasets-edit/${id}`);
    } else if (rolesArray.indexOf(roleWrite) !== -1 || rolesArray.indexOf(roleRead) !== -1) {
      navigate(`/analytics/datasets-details/${id}`);
    }
  };

  const renderExtraColumns = (id: string) => {
    return (
      <td>
        <span><span id={"nonClick"} onClick={() => navigate(`${id}/reports`)} className="normalTable-rowButton">view reports</span></span>
      </td>
    );
  };

  const sortBy = (sortingQuery: string) => {
    getSortBy("datasets", `${sortingQuery}&limit=${currentOffset * 50}&offset=0`);
    setSortedBy(sortingQuery);
  };

  return (
    <div className="datasetsList">
      {validateLoadingStatusNew([allStatusDelete, getSortByStatus, getSortByPaginationStatus]) && <Spinner />}
      {
        <ListPage
          title={"Datasets"}
          sortBy={sortBy}
          timeframeStartTilEnd={true}
          defaultSortCol={"dataset_name"}
          tooltipTxt={"Overview of all the Datasets"}
          columns={columns}
          unblockInput={unblockInput}
          dataUrl={"/analytics/datasets"}
          pageMode={"nonEditable"}
          handleOnDelete={handleOnDelete}
          fetchedData={data}
          onTableScrollAction={onTableScrollAction}
          allStatus={getSortByStatus}
          onRowClickAction={onRowClickAction}
          renderExtraColumns={authCtx.selectedTenant.roles.indexOf(roleRead) !== -1 || authCtx.selectedTenant.roles.indexOf(roleWrite) !== -1? renderExtraColumns :  undefined}
        />
      }
    </div>
  );
};

export default DatasetList;
